import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Award, Lock, ArrowRight, BookOpen, BarChart3, Sparkles, Trophy } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { useAuth } from '@/context/AuthContext';
import { getCertAccess } from '@/lib/certAccess';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ALL_CERTS = [
  {
    code: 'ai-900',
    name: 'AI-900: Azure AI Fundamentals',
    blurb: 'Foundational AI/ML concepts on Azure.',
    path: '/ai-900-exam-prep',
    accent: 'from-cyan-500 to-blue-600',
  },
  {
    code: 'ai-102',
    name: 'AI-102: Azure AI Engineer Associate',
    blurb: 'Build and ship Azure AI solutions.',
    path: '/ai-102-exam-prep',
    accent: 'from-blue-500 to-indigo-600',
  },
  {
    code: 'az-900',
    name: 'AZ-900: Azure Fundamentals',
    blurb: 'Cloud concepts and core Azure services.',
    path: '/az-900-exam-prep',
    accent: 'from-sky-500 to-blue-600',
  },
  {
    code: 'dp-100',
    name: 'DP-100: Azure Data Scientist Associate',
    blurb: 'End-to-end ML on Azure Machine Learning.',
    path: '/dp-100-exam-prep',
    accent: 'from-violet-500 to-fuchsia-600',
  },
  {
    code: 'ab-900',
    name: 'AB-900: Copilot & Agent Administration',
    blurb: 'Administer Microsoft 365 Copilot and agents.',
    path: '/ab-900-exam-prep',
    accent: 'from-purple-500 to-indigo-600',
  },
];

export default function MyLibraryPage() {
  const { user } = useAuth();
  const initialEmail = user?.email || localStorage.getItem('paywall_email') || '';
  const [email, setEmail] = useState(initialEmail);
  const [unlocked, setUnlocked] = useState([]);
  const [progressByCert, setProgressByCert] = useState({});
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);

  const refresh = useCallback(async (lookupEmail) => {
    if (!lookupEmail) return;
    setLoading(true);
    setSearched(true);
    try {
      const data = await getCertAccess(lookupEmail);
      const codes = data.unlocked || [];
      setUnlocked(codes);
      try { localStorage.setItem('paywall_email', lookupEmail.toLowerCase()); } catch {}

      // Pull progress for unlocked certs (by user_id when logged in)
      if (user?.id && codes.length) {
        const progress = {};
        await Promise.all(
          codes.map(async (code) => {
            try {
              const res = await fetch(`${API_URL}/api/${code.replace('-', '')}/progress/${user.id}`);
              progress[code] = await res.json();
            } catch {
              progress[code] = null;
            }
          })
        );
        setProgressByCert(progress);
      } else {
        setProgressByCert({});
      }
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    if (initialEmail) refresh(initialEmail);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleLookup = (e) => {
    e.preventDefault();
    refresh(email.trim());
  };

  const lockedCerts = ALL_CERTS.filter((c) => !unlocked.includes(c.code));
  const unlockedCerts = ALL_CERTS.filter((c) => unlocked.includes(c.code));

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-violet-50/30 to-white pt-24 pb-16" data-testid="my-library-page">
      <div className="max-w-6xl mx-auto px-4 sm:px-6">

        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-end md:justify-between mb-8 gap-4">
          <div>
            <div className="flex items-center gap-2 text-sm text-violet-600 font-medium mb-2">
              <Sparkles className="w-4 h-4" /> Your Pro Library
            </div>
            <h1 className="text-4xl sm:text-5xl font-bold text-slate-900 tracking-tight">
              My Library
            </h1>
            <p className="text-slate-600 mt-2 max-w-2xl">
              Everything you've unlocked, with progress at a glance. Sign in or look up by email
              to see your purchases.
            </p>
          </div>
          {user && (
            <Badge className="bg-violet-100 text-violet-700 self-start md:self-end">
              Signed in as {user.email}
            </Badge>
          )}
        </div>

        {/* Email lookup */}
        {!user && (
          <Card className="mb-8 border-violet-100">
            <CardContent className="pt-6">
              <form onSubmit={handleLookup} className="flex flex-col sm:flex-row gap-3 items-stretch sm:items-end">
                <div className="flex-1">
                  <label className="text-sm text-slate-600 mb-1 block">Find your unlocked certs</label>
                  <Input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="email@you-used-at-checkout.com"
                    data-testid="library-email-input"
                  />
                </div>
                <Button
                  type="submit"
                  className="bg-violet-600 hover:bg-violet-700 text-white"
                  disabled={loading || !email}
                  data-testid="library-lookup-btn"
                >
                  {loading ? 'Looking up...' : 'Find my library'}
                </Button>
              </form>
              <p className="text-xs text-slate-400 mt-3">
                We use this to grant access on the device. Already paid? Use the same email you
                entered at Stripe checkout.
              </p>
            </CardContent>
          </Card>
        )}

        {/* Unlocked grid */}
        {unlockedCerts.length > 0 && (
          <section className="mb-12">
            <h2 className="text-xl font-bold text-slate-900 mb-4 flex items-center gap-2">
              <Trophy className="w-5 h-5 text-violet-600" /> Unlocked ({unlockedCerts.length})
            </h2>
            <div className="grid sm:grid-cols-2 gap-5">
              {unlockedCerts.map((c) => {
                const p = progressByCert[c.code] || {};
                const answered = p.questions_answered || p.total_questions || 0;
                const score = p.readiness_score || p.best_score || 0;
                return (
                  <Card key={c.code} className="overflow-hidden border-violet-100 hover:shadow-lg transition-shadow" data-testid={`unlocked-${c.code}`}>
                    <div className={`h-1.5 bg-gradient-to-r ${c.accent}`} />
                    <CardContent className="pt-5">
                      <div className="flex items-start justify-between mb-3">
                        <div>
                          <div className="flex items-center gap-2">
                            <Award className="w-5 h-5 text-violet-600" />
                            <h3 className="font-bold text-slate-900">{c.name}</h3>
                          </div>
                          <p className="text-sm text-slate-500 mt-1">{c.blurb}</p>
                        </div>
                        <Badge className="bg-green-100 text-green-700">Pro</Badge>
                      </div>

                      <div className="grid grid-cols-2 gap-3 my-4">
                        <div className="rounded-lg bg-slate-50 px-3 py-2">
                          <div className="text-xs text-slate-500 flex items-center gap-1">
                            <BookOpen className="w-3 h-3" /> Questions
                          </div>
                          <div className="text-lg font-semibold text-slate-900">{answered}</div>
                        </div>
                        <div className="rounded-lg bg-slate-50 px-3 py-2">
                          <div className="text-xs text-slate-500 flex items-center gap-1">
                            <BarChart3 className="w-3 h-3" /> Readiness
                          </div>
                          <div className="text-lg font-semibold text-slate-900">{score}%</div>
                        </div>
                      </div>

                      <Link to={c.path}>
                        <Button className="w-full bg-gradient-to-r from-violet-600 to-fuchsia-600 hover:from-violet-700 hover:to-fuchsia-700 text-white" data-testid={`open-${c.code}`}>
                          Open Exam Coach <ArrowRight className="w-4 h-4 ml-2" />
                        </Button>
                      </Link>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </section>
        )}

        {/* Empty state */}
        {searched && unlockedCerts.length === 0 && !loading && (
          <Card className="mb-12 border-dashed">
            <CardContent className="py-10 text-center">
              <Lock className="w-10 h-10 text-slate-300 mx-auto mb-3" />
              <h3 className="text-lg font-semibold text-slate-700 mb-1">No certs unlocked for this email</h3>
              <p className="text-sm text-slate-500">
                Browse certifications below — each unlocks Exam Simulation, Weak-Areas practice
                and AI Coach Chat for $39 (one-time).
              </p>
            </CardContent>
          </Card>
        )}

        {/* Locked / available certs */}
        <section>
          <h2 className="text-xl font-bold text-slate-900 mb-4 flex items-center gap-2">
            <Lock className="w-5 h-5 text-slate-400" /> Available to unlock
          </h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {lockedCerts.map((c) => (
              <Link to={c.path} key={c.code} data-testid={`locked-${c.code}`}>
                <Card className="hover:shadow-md hover:-translate-y-0.5 transition-all h-full">
                  <div className={`h-1 bg-gradient-to-r ${c.accent}`} />
                  <CardContent className="pt-5">
                    <Award className="w-6 h-6 text-slate-400 mb-2" />
                    <h3 className="font-semibold text-slate-900 text-sm leading-snug mb-1">{c.name}</h3>
                    <p className="text-xs text-slate-500 mb-3">{c.blurb}</p>
                    <div className="flex items-center justify-between">
                      <span className="text-violet-600 font-semibold">$39</span>
                      <ArrowRight className="w-4 h-4 text-slate-400" />
                    </div>
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
        </section>
      </div>
    </div>
  );
}
